'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetUserActivityField } from '@morfi/backend-sdk-v2/client';
import { useLocale, useTranslations } from 'next-intl';
import type { Ref } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { ThumbWithOrigin } from '@/components/market/identity/identity-marks';
import type { MarketIdentityData } from '@/components/market/identity/market-identity';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { ArrowDownLeft01Icon, FlashIcon, GiftIcon, ShoppingCart01Icon, SparklesIcon, StarIcon } from '@/components/ui/icon/glyphs';
import { OutcomePill } from '@/components/ui/outcome-pill';
import { StatusPill } from '@/components/ui/status-pill';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { useMarketDetailPrefetchIntent } from '@/lib/sdk/prefetch';
import { cn } from '@/lib/ui/utils';
import { ActivityShareButton } from './activity-share-button';
const LEADING_CONTAINER_CLASS = 'size-12 shrink-0 rounded-panel-inset' as const;
type ActivityKind = 'trade' | 'claim' | 'claim_backer_fees' | 'back' | 'unback' | 'boost' | 'gift_received' | 'gift_sent';
type TradeSide = 'buy' | 'sell';
type DirectionTone = 'credit' | 'debit' | 'neutral';
function truncatePubkey(pubkey: string | null | undefined): string {
  if (typeof pubkey !== 'string' || pubkey.length === 0) return '';
  if (pubkey.length <= 8) return pubkey;
  return `${pubkey.slice(0, 4)}…${pubkey.slice(-4)}`;
}
const DIRECTION_TONE_CLASS: Readonly<Record<DirectionTone, string>> = {
  credit: 'text-data-profit',
  debit: 'text-data-loss',
  neutral: 'text-foreground-secondary'
};
interface RowVisualSpec {
  glyph: IconSvgElement;
  iconTint: string;
  tone: DirectionTone;
}
function getVisualSpec(kind: ActivityKind, side: TradeSide | null): RowVisualSpec {
  switch (kind) {
    case 'trade':
      if (side === 'sell') {
        return {
          glyph: ArrowDownLeft01Icon,
          iconTint: 'text-trade-sell',
          tone: 'debit'
        };
      }
      return {
        glyph: ShoppingCart01Icon,
        iconTint: 'text-trade-buy',
        tone: 'credit'
      };
    case 'claim':
      return {
        glyph: SparklesIcon,
        iconTint: 'text-feedback-success-content',
        tone: 'credit'
      };
    case 'claim_backer_fees':
      return {
        glyph: SparklesIcon,
        iconTint: 'text-feedback-success-content',
        tone: 'credit'
      };
    case 'back':
      return {
        glyph: StarIcon,
        iconTint: 'text-feedback-success-content',
        tone: 'credit'
      };
    case 'unback':
      return {
        glyph: StarIcon,
        iconTint: 'text-feedback-error-content',
        tone: 'debit'
      };
    case 'boost':
      return {
        glyph: FlashIcon,
        iconTint: 'text-feedback-warning-content',
        tone: 'neutral'
      };
    case 'gift_received':
      return {
        glyph: GiftIcon,
        iconTint: 'text-feedback-success-content',
        tone: 'credit'
      };
    case 'gift_sent':
      return {
        glyph: GiftIcon,
        iconTint: 'text-foreground-secondary',
        tone: 'debit'
      };
  }
}
type VerbKey = 'activity_verb_buy' | 'activity_verb_sell' | 'activity_verb_claim' | 'activity_verb_claim_backer_fees' | 'activity_verb_back' | 'activity_verb_unback' | 'activity_verb_boost' | 'activity_verb_gift_sent' | 'activity_verb_gift_received';
function getVerbKey(kind: ActivityKind, side: TradeSide | null): VerbKey {
  switch (kind) {
    case 'trade':
      return side === 'sell' ? 'activity_verb_sell' : 'activity_verb_buy';
    case 'claim':
      return 'activity_verb_claim';
    case 'claim_backer_fees':
      return 'activity_verb_claim_backer_fees';
    case 'back':
      return 'activity_verb_back';
    case 'unback':
      return 'activity_verb_unback';
    case 'boost':
      return 'activity_verb_boost';
    case 'gift_received':
      return 'activity_verb_gift_received';
    case 'gift_sent':
      return 'activity_verb_gift_sent';
  }
}
interface LeadingVisualProps {
  marketPubkey: string | null;
  coverImageUrl: string | null;
  nsfw: boolean | null;
  spec: RowVisualSpec;
  kind: ActivityKind;
  marketType: MarketIdentityData['marketType'];
}
function LeadingVisual(t0) {
  const $ = _c(13);
  const {
    marketPubkey,
    coverImageUrl,
    nsfw,
    spec,
    kind,
    marketType
  } = t0;
  const hasMarketImage = marketPubkey !== null && marketPubkey.length > 0 && kind !== "gift_sent" && kind !== "gift_received";
  if (hasMarketImage && marketPubkey !== null) {
    const t1 = nsfw ?? false;
    let t2;
    if ($[0] !== coverImageUrl || $[1] !== marketPubkey || $[2] !== t1) {
      t2 = <OptimizedImage variant="thumbnail" entity={{
        kind: "market",
        pubkey: marketPubkey,
        category: "",
        coverImageUrl,
        nsfw: t1
      }} alt="" size="size-12" rounded="rounded-panel-inset" preview={false} className={LEADING_CONTAINER_CLASS} />;
      $[0] = coverImageUrl;
      $[1] = marketPubkey;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== marketType || $[5] !== t2) {
      t3 = <ThumbWithOrigin marketType={marketType}>{t2}</ThumbWithOrigin>;
      $[4] = marketType;
      $[5] = t2;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  let t1;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(LEADING_CONTAINER_CLASS, "flex items-center justify-center bg-background-tertiary");
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const t2 = spec.glyph;
  let t3;
  if ($[8] !== spec.iconTint) {
    t3 = cn("size-6", spec.iconTint);
    $[8] = spec.iconTint;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== spec.glyph || $[11] !== t3) {
    t4 = <div className={t1} data-testid="activity-row-icon-square"><Icon icon={t2} className={t3} /></div>;
    $[10] = spec.glyph;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
interface PrimaryTitleProps {
  kind: ActivityKind;
  marketPubkey: string | null;
  marketHref: string | undefined;
  marketTitle: string | null;
  counterpartyHandle: string | null;
}
function PrimaryTitle(t0) {
  const $ = _c(12);
  const {
    kind,
    marketPubkey,
    marketHref,
    marketTitle,
    counterpartyHandle
  } = t0;
  const t = useTranslations("profile.stats");
  if (kind === "gift_received" || kind === "gift_sent") {
    let t1;
    if ($[0] !== counterpartyHandle || $[1] !== t) {
      t1 = counterpartyHandle !== null && counterpartyHandle.length > 0 ? `@${counterpartyHandle}` : t("activity_anonymous_morfian");
      $[0] = counterpartyHandle;
      $[1] = t;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    const label = t1;
    let t2;
    if ($[3] !== label) {
      t2 = <span className="truncate text-body font-medium text-foreground-primary" data-testid="activity-row-title-gift">{label}</span>;
      $[3] = label;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  if (marketHref !== undefined) {
    let t1;
    if ($[5] !== marketPubkey || $[6] !== marketTitle) {
      t1 = marketTitle !== null && marketTitle.length > 0 ? marketTitle : truncatePubkey(marketPubkey);
      $[5] = marketPubkey;
      $[6] = marketTitle;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    const label_0 = t1;
    let t2;
    if ($[8] !== label_0 || $[9] !== marketHref) {
      t2 = <Link href={marketHref} className="relative z-10 truncate text-body font-medium text-foreground-primary hover:underline" data-testid="activity-row-title-link">{label_0}</Link>;
      $[8] = label_0;
      $[9] = marketHref;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    return t2;
  }
  let t1;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="truncate text-body font-medium text-foreground-primary" data-testid="activity-row-title-fallback" />;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  return t1;
}
interface SecondaryLineProps {
  kind: ActivityKind;
  side: TradeSide | null;
  amountD6: string;
  timestamp: string;
  outcomeLabel: string | null;
  outcomeIndex: number | null;
  spec: RowVisualSpec;
}
function SecondaryLine(t0) {
  const $ = _c(29);
  const {
    kind,
    side,
    amountD6,
    timestamp,
    outcomeLabel,
    outcomeIndex,
    spec
  } = t0;
  const t = useTranslations("profile.stats");
  const locale = useLocale();
  const tTimeIntl = useTranslations("time");
  let t1;
  if ($[0] !== tTimeIntl) {
    t1 = (key, params) => tTimeIntl(key as Parameters<typeof tTimeIntl>[0], params);
    $[0] = tTimeIntl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const tTime = t1;
  let t2;
  if ($[2] !== locale || $[3] !== tTime || $[4] !== timestamp) {
    t2 = formatRelativeTime(timestamp, tTime, {
      style: "short",
      locale
    });
    $[2] = locale;
    $[3] = tTime;
    $[4] = timestamp;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const relative = t2;
  const {
    formatD6
  } = useFormattedCurrency();
  let t3;
  if ($[6] !== amountD6 || $[7] !== formatD6) {
    t3 = formatD6(amountD6);
    $[6] = amountD6;
    $[7] = formatD6;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const amountText = t3;
  let t4;
  if ($[9] !== kind || $[10] !== side || $[11] !== t) {
    t4 = t(getVerbKey(kind, side));
    $[9] = kind;
    $[10] = side;
    $[11] = t;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const verb = t4;
  const showTime = relative.length > 0;
  let t5;
  if ($[13] !== amountText || $[14] !== spec.tone) {
    t5 = spec.tone === "credit" || spec.tone === "debit" ? <StatusPill tone={spec.tone === "credit" ? "success" : "destructive"} size="xs" data-testid="activity-row-status-pill"><span className="font-mono tabular-nums" data-testid="activity-row-amount">{amountText}</span></StatusPill> : <span className={cn("shrink-0 font-mono tabular-nums", DIRECTION_TONE_CLASS[spec.tone])} data-testid="activity-row-amount">{amountText}</span>;
    $[13] = amountText;
    $[14] = spec.tone;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const renderAmount = t5;
  let t6;
  if ($[16] !== verb) {
    t6 = <span className="shrink-0 text-foreground-tertiary" data-testid="activity-row-kind">{verb}</span>;
    $[16] = verb;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== outcomeIndex || $[19] !== outcomeLabel) {
    t7 = typeof outcomeLabel === "string" && outcomeLabel.length > 0 && typeof outcomeIndex === "number" && <OutcomePill outcome={{
      title: outcomeLabel,
      index: outcomeIndex
    }} label={outcomeLabel} size="sm" labelLayout="natural" className="items-center py-0.5" />;
    $[18] = outcomeIndex;
    $[19] = outcomeLabel;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== relative || $[22] !== showTime) {
    t8 = showTime && <><span aria-hidden="true" className="shrink-0 text-foreground-tertiary">·</span><span className="shrink-0 tabular-nums text-foreground-tertiary" data-testid="activity-row-timestamp">{relative}</span></>;
    $[21] = relative;
    $[22] = showTime;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== renderAmount || $[25] !== t6 || $[26] !== t7 || $[27] !== t8) {
    t9 = <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-caption text-foreground-secondary" data-testid="activity-row-secondary-line">{t6}{renderAmount}{t7}{t8}</div>;
    $[24] = renderAmount;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
}
export interface ActivityRowProps {
  /** Stable row id (chain signature or gift opaqueId) — drives field lookups. */
  rowId: string;
  /** Whether the viewed portfolio belongs to the signed-in user. */
  isOwn: boolean;
  /** Viewport-tracking ref forwarded from the SDK `<Items>` render prop. */
  ref?: Ref<HTMLLIElement>;
}

/**
 * Spec 088 + activity-feed restoration — single Recent Activity row.
 *
 * Rendered inside an `<Items>` render-prop from `useGetUserActivityInfinite`.
 * Each visible row reads its own fields off `useGetUserActivityField` and
 * switches its visual on `kind`. The 7 kinds are: `trade` (buy/sell),
 * `claim` (prize claim), `back` / `unback`, `boost`, `gift_sent`,
 * `gift_received`. Market title falls back to a truncated pubkey when the
 * SDK row doesn't carry one; the leading thumbnail is rendered via the
 * standard market-pubkey-derived `<OptimizedImage>` lookup.
 */
export function ActivityRow(props) {
  const $ = _c(61);
  const {
    rowId,
    isOwn,
    ref
  } = props;
  const marketType = useGetUserActivityField(rowId, "marketType");
  const kind = (useGetUserActivityField(rowId, "kind") ?? "trade") as ActivityKind;
  const market = useGetUserActivityField(rowId, "market") as string | undefined ?? null;
  const amountD6 = useGetUserActivityField(rowId, "amountD6") as string | null ?? "0";
  const ts = useGetUserActivityField(rowId, "ts") as string | null ?? "";
  const side = useGetUserActivityField(rowId, "side") as TradeSide | undefined ?? null;
  const counterpartyHandle = useGetUserActivityField(rowId, "counterpartyHandle") as string | undefined ?? null;
  const trader = useGetUserActivityField(rowId, "trader") as string | undefined ?? null;
  const outcomeLabel = useGetUserActivityField(rowId, "outcomeLabel") as string | undefined ?? null;
  const outcomeIndex = useGetUserActivityField(rowId, "outcomeIndex") as number | undefined ?? null;
  const marketTitle = useGetUserActivityField(rowId, "marketTitle") as string | undefined ?? null;
  const marketCoverImageUrl = useGetUserActivityField(rowId, "marketCoverImageUrl") as string | undefined ?? null;
  const marketCoverImageNsfw = useGetUserActivityField(rowId, "marketCoverImageNsfw") as boolean | undefined ?? null;
  let t0;
  if ($[0] !== kind || $[1] !== side) {
    t0 = getVisualSpec(kind, side);
    $[0] = kind;
    $[1] = side;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const spec = t0;
  const isGift = kind === "gift_sent" || kind === "gift_received";
  const opensProposal = kind === "back" || kind === "unback";
  let t1;
  if ($[3] !== isGift || $[4] !== market || $[5] !== opensProposal) {
    t1 = marketHrefFor(market, isGift, opensProposal);
    $[3] = isGift;
    $[4] = market;
    $[5] = opensProposal;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const marketHref = t1;
  let t2;
  if ($[7] !== market || $[8] !== marketTitle) {
    t2 = marketTitle !== null && marketTitle.length > 0 ? marketTitle : truncatePubkey(market);
    $[7] = market;
    $[8] = marketTitle;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const titleLabel = t2;
  const dataWarm = useMarketDetailPrefetchIntent(isGift || opensProposal ? null : market);
  const intent = useIntentRoutePrefetch(marketHref, dataWarm.onMouseEnter);
  const t3 = marketHref !== undefined ? "transition-colors hover:bg-background-tertiary" : "";
  let t4;
  if ($[10] !== t3) {
    t4 = cn("relative flex items-center gap-3 rounded-panel-row border border-border-secondary px-3 py-2.5", t3);
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== intent || $[13] !== marketHref || $[14] !== titleLabel) {
    t5 = marketHref !== undefined ? <Link href={marketHref} prefetch={false} aria-label={titleLabel} className="absolute inset-0 z-0 rounded-panel-row" {...intent} /> : null;
    $[12] = intent;
    $[13] = marketHref;
    $[14] = titleLabel;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== kind || $[17] !== market || $[18] !== marketCoverImageNsfw || $[19] !== marketCoverImageUrl || $[20] !== marketType || $[21] !== spec) {
    t6 = <LeadingVisual marketPubkey={market} coverImageUrl={marketCoverImageUrl} nsfw={marketCoverImageNsfw} spec={spec} kind={kind} marketType={marketType} />;
    $[16] = kind;
    $[17] = market;
    $[18] = marketCoverImageNsfw;
    $[19] = marketCoverImageUrl;
    $[20] = marketType;
    $[21] = spec;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== counterpartyHandle || $[24] !== kind || $[25] !== market || $[26] !== marketHref || $[27] !== marketTitle) {
    t7 = <PrimaryTitle kind={kind} marketPubkey={market} marketHref={marketHref} marketTitle={marketTitle} counterpartyHandle={counterpartyHandle} />;
    $[23] = counterpartyHandle;
    $[24] = kind;
    $[25] = market;
    $[26] = marketHref;
    $[27] = marketTitle;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== amountD6 || $[30] !== kind || $[31] !== outcomeIndex || $[32] !== outcomeLabel || $[33] !== side || $[34] !== spec || $[35] !== ts) {
    t8 = <SecondaryLine kind={kind} side={side} amountD6={amountD6} timestamp={ts} outcomeLabel={outcomeLabel} outcomeIndex={outcomeIndex} spec={spec} />;
    $[29] = amountD6;
    $[30] = kind;
    $[31] = outcomeIndex;
    $[32] = outcomeLabel;
    $[33] = side;
    $[34] = spec;
    $[35] = ts;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== t7 || $[38] !== t8) {
    t9 = <div className="flex min-w-0 flex-1 flex-col gap-2">{t7}{t8}</div>;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  let t10;
  if ($[40] !== amountD6 || $[41] !== kind || $[42] !== market || $[43] !== outcomeIndex || $[44] !== rowId || $[45] !== side || $[46] !== trader) {
    t10 = kind === "trade" && side !== null && typeof outcomeIndex === "number" && market !== null && market.length > 0 && trader !== null && trader.length > 0 && <span className="relative z-10 shrink-0"><ActivityShareButton activityType="trade" signature={rowId} marketPubkey={market} traderPubkey={trader} direction={side} amountD6={amountD6} outcomeIndex={outcomeIndex} /></span>;
    $[40] = amountD6;
    $[41] = kind;
    $[42] = market;
    $[43] = outcomeIndex;
    $[44] = rowId;
    $[45] = side;
    $[46] = trader;
    $[47] = t10;
  } else {
    t10 = $[47];
  }
  let t11;
  if ($[48] !== isOwn || $[49] !== kind || $[50] !== market || $[51] !== titleLabel) {
    t11 = kind === "boost" && isOwn && market !== null && market.length > 0 ? <span className="relative z-10 shrink-0"><ActivityShareButton activityType="boost" marketPubkey={market} marketTitle={titleLabel} /></span> : null;
    $[48] = isOwn;
    $[49] = kind;
    $[50] = market;
    $[51] = titleLabel;
    $[52] = t11;
  } else {
    t11 = $[52];
  }
  let t12;
  if ($[53] !== ref || $[54] !== t10 || $[55] !== t11 || $[56] !== t4 || $[57] !== t5 || $[58] !== t6 || $[59] !== t9) {
    t12 = <li ref={ref} className={t4} data-testid="activity-row">{t5}{t6}{t9}{t10}{t11}</li>;
    $[53] = ref;
    $[54] = t10;
    $[55] = t11;
    $[56] = t4;
    $[57] = t5;
    $[58] = t6;
    $[59] = t9;
    $[60] = t12;
  } else {
    t12 = $[60];
  }
  return t12;
}
function marketHrefFor(market: string | null, isGift: boolean, opensProposal: boolean): string | undefined {
  if (isGift || market === null || market.length === 0) return undefined;
  return opensProposal ? `/proposals/${market}` : `/markets/${market}`;
}
export function ActivityRowSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className={cn(LEADING_CONTAINER_CLASS)} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <li className="flex items-center gap-3 rounded-panel-row border border-border-secondary px-3 py-2.5" aria-hidden="true" data-testid="activity-row-skeleton">{t0}<div className="flex min-w-0 flex-1 flex-col gap-2"><Skeleton className="h-4 w-2/3" /><Skeleton className="h-6 w-1/2 rounded-pill" /></div><Skeleton className="h-3 w-8 shrink-0" /></li>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}